import { useModal } from "context";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { FormButton } from "reusableComponents";
import React, { useEffect, useState } from "react";
import { addCard, deleteCard, fetchWallet } from "services/wallet.services";
import DeleteCardConfirmationModal from "modals/DeleteCardConfirmationModal";
import { visa, mastercard, diners, maestro, credit } from "assets/icons/cards";
import { default as poweredByMng } from "assets/images/powered-by-mangopay.png";

import styles from "./styles.module.scss";

const PaymentMethods = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("global");
  const [walletData, setWalletData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { openModal, closeModal } = useModal();
  const token = useSelector((state) => state.auth.token);

  const paymentMethods = [
    { name: "Diners Club", image: diners },
    { name: "MasterCard", image: mastercard },
    { name: "Maestro", image: maestro },
    { name: "Visa", image: visa },
    { name: "iDeal, Bancontact/Mister Cash, Paylib", image: credit },
  ];

  useEffect(() => {
    const getWallet = async () => {
      setLoading(true);
      try {
        const data = await fetchWallet(token);
        setWalletData(data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    getWallet();
  }, [token]);

  const handleAddCard = async () => {
    try {
      const response = await addCard(token);
      if (response?.data?.redirectURL) {
        window.location.href = response.data.redirectURL;
      }
    } catch (error) {
      console.error("Error adding card:", error);
    }
  };

  const handleDeleteCard = async () => {
    setLoading(true);
    try {
      const response = await deleteCard(token);
      if (response?.success) {
        // Update wallet data after successful deletion
        const updatedWallet = await fetchWallet(token);
        setWalletData(updatedWallet);
        closeModal();
      } else {
        console.error(
          "Failed to delete card:",
          response?.message || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Error deleting card:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`${styles.paymentMethods} ${loading ? styles.loading : ""}`}
    >
      <h2 className={styles.title}>{t("dashboard.cards.title")}</h2>
      <div className={styles.body}>
        {walletData?.hasWallet ? (
          <div className={styles.cardDetails}>
            <div>
              <strong> {t("dashboard.cards.cardNumber")}</strong>{" "}
              {walletData.card.number}
            </div>
            <div>
              <strong>{t("dashboard.cards.cardType")}:</strong>{" "}
              {walletData.card.type}
            </div>
            <div>
              <strong>{t("dashboard.cards.validity")}:</strong>{" "}
              {walletData.card.expirationDate}
            </div>
            <FormButton
              label={t("dashboard.cards.deleteCard")}
              variant="submit"
              onClick={() =>
                openModal({
                  content: (
                    <DeleteCardConfirmationModal
                      handleDelete={handleDeleteCard}
                    />
                  ),
                  title: t("dashboard.cards.confirmationTitle"),
                })
              }
            />
          </div>
        ) : (
          <div className={styles.cardsContainer}>
            {paymentMethods.map((method, index) => (
              <div key={index} className={styles.card}>
                <img
                  src={method.image}
                  alt={method.name}
                  className={styles.cardImage}
                />
                <div className={styles.tooltip}>{method.name}</div>
              </div>
            ))}
            <FormButton
              label={t("dashboard.cards.addCard")}
              variant="submit"
              onClick={handleAddCard}
            />
          </div>
        )}
        <span
          className={styles.secureWebsiteText}
          dangerouslySetInnerHTML={{
            __html: t("dashboard.cards.secureWebsite"),
          }}
        ></span>
      </div>
      <div className={styles.poweredByContainer}>
        <img
          className={styles.powerdByMngImg}
          src={poweredByMng}
          alt="Powered by Mangopay"
        />
      </div>{" "}
    </div>
  );
};

export default PaymentMethods;
